import { isProductionEnvironment, isTrackingEnabledInLocalDev } from './env-utils.ts'

/**
 * Safe localStorage access helpers.
 * localStorage can throw SecurityError in restricted browser contexts:
 * - In-app browsers (Facebook, Snapchat, Instagram webviews)
 * - Private/Incognito mode in some browsers
 * - Cross-origin iframes with restrictive sandbox attributes
 * - Browsers with disabled cookies
 */
function safeLocalStorageGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export function safeLocalStorageSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Silently ignore - localStorage access denied
  }
}

/**
 * Google Ads Conversion Linker Tag
 *
 * This ensures that the gclid parameter is properly stored and linked
 * for conversion tracking across the user journey.
 *
 * The Conversion Linker Tag:
 * 1. Stores the gclid in a first-party cookie
 * 2. Ensures the gclid persists across page loads and sessions
 * 3. Enables proper conversion attribution in Google Ads
 */

function initializeGoogleAdsConversionLinker(): void {
  if (!isProductionEnvironment() && !isTrackingEnabledInLocalDev()) {
    // In development without tracking flag, just store in localStorage for testing
    const urlParams = new URLSearchParams(window.location.search)
    const gclid = urlParams.get('gclid')

    if (gclid) {
      safeLocalStorageSetItem('gclid', gclid)
    }
    return
  }

  // Check if gclid is present in URL
  const urlParams = new URLSearchParams(window.location.search)
  const gclid = urlParams.get('gclid')

  if (!gclid) {
    return
  }

  // Store gclid in a first-party cookie with proper expiration (30 days)
  const expirationDays = 30
  const expirationSeconds = expirationDays * 24 * 60 * 60

  document.cookie = `_gcl_aw=${gclid}; max-age=${expirationSeconds}; path=/; SameSite=Lax`

  // Also store in localStorage as backup
  safeLocalStorageSetItem('gclid', gclid)
}

/**
 * Enhanced gclid tracking with cross-domain support
 *
 * This function should be called on the landing page to ensure
 * the gclid is properly captured and transmitted to the app.
 */
export function trackGoogleAdsClick(): void {
  const urlParams = new URLSearchParams(window.location.search)
  const gclid = urlParams.get('gclid')

  if (!gclid) {
    return
  }

  // Initialize the conversion linker
  initializeGoogleAdsConversionLinker()

  // Send to GTM dataLayer for enhanced tracking
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'google_ads_click',
      gclid: gclid,
    })
  }

  // Store additional metadata for attribution
  const clickData = {
    gclid,
    userAgent: navigator.userAgent,
    referrer: document.referrer,
    landingPage: window.location.href,
  }

  safeLocalStorageSetItem('google_ads_click_data', JSON.stringify(clickData))
}

/**
 * Get stored gclid for conversion tracking
 */
function getStoredGclid(): string | null {
  // First try to get from cookie (preferred method)
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === '_gcl_aw' && value) {
      return value
    }
  }

  // Fallback to localStorage
  return safeLocalStorageGetItem('gclid')
}

/**
 * Validates gclidSource values to ensure type safety
 */
export function validateGclidSource(value: string | undefined): 'url' | 'cookie' | undefined {
  if (value === undefined || value === 'url' || value === 'cookie') {
    return value
  }

  return undefined
}

/**
 * Enhanced session tracking with gclid validation
 */
export function getEnhancedTrackingInfo(searchParams: URLSearchParams): {
  gclid: string | undefined
  gclidSource: 'url' | 'cookie' | undefined
} {
  const gclid = searchParams.get('gclid') || getStoredGclid()

  // Determine source without type casting
  let gclidSource: string | undefined
  if (gclid) {
    gclidSource = searchParams.get('gclid') ? 'url' : 'cookie'
  } else {
    gclidSource = undefined
  }

  return {
    gclid: gclid || undefined,
    gclidSource: validateGclidSource(gclidSource),
  }
}
